import { Button, Checkbox, Form, Input, Modal, Table, Typography, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { serverFetch } from "../core/server";
import "./users-editor.css";

const emptyValues = (showAdminFlag) => ({
  name: "",
  email: "",
  admin: showAdminFlag
});

const UsersEditor = ({ listUrl, superAdmin = false, canEdit = false, showAdminFlag = false }) => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadRows = useCallback(() => {
    setLoading(true);
    serverFetch(listUrl)
      .then(setRows)
      .catch((e) => message.error(e.userMessage || e.message || "Не удалось загрузить пользователей"))
      .finally(() => setLoading(false));
  }, [listUrl]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    form.setFieldsValue(selected
      ? { name: selected.name, email: selected.email, admin: selected.admin }
      : emptyValues(showAdminFlag));
  }, [form, selected, showAdminFlag]);

  const onReset = () => {
    setSelected(null);
    form.setFieldsValue(emptyValues(showAdminFlag));
  };

  const onSave = (values) => {
    setSaving(true);
    const body = {
      name: values.name.trim(),
      email: values.email.trim(),
      admin: showAdminFlag ? !!values.admin : true,
      superAdmin
    };
    const request = selected
      ? serverFetch(`users/${selected.id}`, { method: "PUT", bodyData: body })
      : serverFetch("users", { method: "POST", bodyData: body });

    request
      .then((saved) => {
        message.success(selected ? "Пользователь сохранён" : "Пользователь добавлен");
        setSelected(saved);
        loadRows();
      })
      .catch((e) => message.error(e.userMessage || e.message || "Не удалось сохранить пользователя"))
      .finally(() => setSaving(false));
  };

  const onDelete = () => {
    if (!selected || selected.current) return;
    Modal.confirm({
      title: "Удалить пользователя?",
      content: `${selected.name} (${selected.email})`,
      okText: "Удалить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: () =>
        serverFetch(`users/${selected.id}`, { method: "DELETE" })
          .then(() => {
            message.success("Пользователь удалён");
            onReset();
            loadRows();
          })
          .catch((e) => {
            message.error(e.userMessage || e.message || "Не удалось удалить пользователя");
            return Promise.reject();
          })
    });
  };

  const columns = [
    { title: "Имя", dataIndex: "name", key: "name" },
    { title: "Почта", dataIndex: "email", key: "email" },
    ...(showAdminFlag
      ? [{
          title: "Админ",
          dataIndex: "admin",
          key: "admin",
          width: 90,
          render: (value) => (value ? "да" : "—")
        }]
      : [])
  ];

  return (
    <div className="users-editor">
      <Table
        className="users-editor-table"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
        rowClassName={(row) => (selected?.id === row.id ? "is-selected" : "")}
        onRow={(row) => ({
          onClick: () => setSelected(row)
        })}
      />

      {canEdit && (
        <Form
          className="users-editor-form"
          form={form}
          layout="vertical"
          initialValues={emptyValues(showAdminFlag)}
          onFinish={onSave}
        >
          <Typography.Paragraph type="secondary" className="users-editor-caption">
            {selected
              ? selected.current
                ? "Ваша учётная запись"
                : `Редактирование: ${selected.name}`
              : "Новый пользователь"}
          </Typography.Paragraph>
          <Form.Item name="name" label="Имя" rules={[{ required: true, message: "Укажите имя" }]}>
            <Input placeholder="Имя" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Почта"
            rules={[
              { required: true, message: "Укажите почту" },
              { type: "email", message: "Некорректная почта" }
            ]}
          >
            <Input placeholder="user@company.ru" />
          </Form.Item>
          {showAdminFlag && (
            <Form.Item name="admin" valuePropName="checked">
              <Checkbox>Администратор компании</Checkbox>
            </Form.Item>
          )}
          <div className="users-editor-actions">
            <Button type="primary" htmlType="submit" loading={saving}>
              Сохранить
            </Button>
            <Button onClick={onReset}>Добавить</Button>
            {selected && !selected.current && (
              <Button danger onClick={onDelete}>
                Удалить
              </Button>
            )}
          </div>
        </Form>
      )}
    </div>
  );
};

export default UsersEditor;
